// Lógica pura (sin React/Firebase) para calcular la posición de cada
// atleta dentro de una "subclasificación" — una variable adicional a
// modalidad/rama/categoría (ej. "mejor de tal ciudad", "mejor de tal
// equipo"), definida por el admin en Crono > Configuración (en el panel
// admin, beta/dashportV6.0). Este archivo es un PORT manual de la versión
// equivalente en ese repo — no hay código compartido entre ambos, cada uno
// vive en su propio proyecto React/npm independiente.
//
// Igual que reportsLogic.jsx (ResultadosCategoria/ImprimirDatos), opera
// sobre resultados YA posicionados por calculatePositions — reutiliza
// tiempoOficialSegundos, ya calculado ahí, y solo re-agrupa/ordena/filtra
// para esta vista. No recalcula pG/pR/pCat ni los toca.
//
// El valor de cada atleta para una subclasificación vive directo en
// resultado.subclasificaciones[subclasifId] (escrito desde el panel admin)
// — acá solo se lee, nunca se escribe (esta app no edita subclasificaciones).

import { helperMultisort } from "../../../services/utils";

// Posición de cada atleta DENTRO de su grupo: mismo valor de la variable +
// misma modalidad + misma rama. No se puede comparar tiempos de distancias
// distintas, por eso modalidad entra en el agrupamiento aunque la variable
// en sí (ciudad, equipo, etc.) no tenga nada que ver con la distancia.
//
// Entran TODOS los atletas con un valor no vacío para esta subclasificación,
// hayan terminado o no (igual que los demás reportes, que muestran a los
// DNF/DNS/DQ en vez de simplemente omitirlos). Los finishers (pG > 0) se
// numeran por tiempo dentro de su grupo; el resto queda con pSub null.
export function calcularSubclasificacion(resultados, subclasifId) {
  const conValor = (resultados || [])
    .map((r) => ({
      ...r,
      valorSubclasif: r.subclasificaciones && r.subclasificaciones[subclasifId],
    }))
    .filter(
      (r) =>
        r.valorSubclasif !== undefined &&
        r.valorSubclasif !== null &&
        r.valorSubclasif !== ""
    )
    .map((r) => ({ ...r, ordenFinisher: r.pG > 0 ? 0 : 1 }));

  const ordenado = helperMultisort(
    conValor,
    ["valorSubclasif", "codMod", "gen", "ordenFinisher", "tiempoOficialSegundos"],
    ["ASC", "ASC", "ASC", "ASC", "ASC"]
  );

  const contadores = {};
  return ordenado.map((r) => {
    if (r.pG <= 0) return { ...r, pSub: null };
    const idGrupo = r.valorSubclasif + "-" + r.codMod + "-" + r.gen;
    contadores[idGrupo] = (contadores[idGrupo] || 0) + 1;
    return { ...r, pSub: contadores[idGrupo] };
  });
}

const NOMBRE_RAMA = { V: "Varonil", F: "Femenil", M: "Mixto" };

// Arma "pseudo-modalidades" de ganadores de subclasificación, en la MISMA
// forma que ya produce buildWinnersBoardData (reportsLogic.jsx) — así
// CeremoniaPremiacion.jsx/WinnersBoardCompact.jsx las reciben concatenadas
// al array normal sin saber que existen subclasificaciones (cero cambios en
// esos componentes). Una subclasificación sin cantidadGanadores configurado
// (o en 0) simplemente no aporta nada.
export function buildSubclasificacionWinnersBoardData(eventResults, subclasificaciones, eventSubEvents) {
  const grupos = [];

  Object.keys(subclasificaciones || {}).forEach((id) => {
    const def = subclasificaciones[id];
    if (!def || !def.cantidadGanadores) return;

    const ganadores = calcularSubclasificacion(eventResults, id).filter(
      (r) => r.pSub !== null && r.pSub <= def.cantidadGanadores
    );
    if (!ganadores.length) return;

    const categoriasMap = new Map();
    ganadores.forEach((r) => {
      const key = r.valorSubclasif + "|" + r.codMod + "|" + r.gen;
      if (!categoriasMap.has(key)) {
        const info = eventSubEvents && eventSubEvents["modalidad_" + r.codMod];
        const descMod = info ? info.descripcionModalidad : "Modalidad " + r.codMod;
        categoriasMap.set(key, {
          cat: r.valorSubclasif + " - " + descMod,
          gen: r.gen,
          codCat: null,
          esAbsoluto: false,
          generoLabel: NOMBRE_RAMA[r.gen] || r.gen,
          ganadores: [],
        });
      }
      categoriasMap.get(key).ganadores.push({
        posicion: r.pSub,
        num: r.num,
        nombre: r.nombre,
        tiempo: (r.to || r.toMS) ? String(r.to || r.toMS).split(".")[0] : r.to,
      });
    });

    grupos.push({
      codMod: "subclasif_" + id,
      descripcionModalidad: def.nombre,
      categorias: Array.from(categoriasMap.values()).sort((a, b) => a.cat.localeCompare(b.cat)),
    });
  });

  return grupos;
}
